// AuthStore — single source of truth for current user session.
// Hydrates from localStorage on mount, calls /api/auth/me to validate.
// Exposes useAuth() hook + <RequireAuth> route guard.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, apiError, AUTH_SESSION_EXPIRED_EVENT, setBearerToken, clearBearerToken } from "../lib/api";
import { setSupabaseRealtimeAuth } from "../lib/supabaseClient";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

// Refresh the Supabase Realtime JWT 15 minutes before the canonical
// 1-hour TTL expires (the backend's `supabase_jwt.TOKEN_TTL`). Keeping
// the realtime channel armed under RLS without forcing the user
// through an interactive auth step.
const REALTIME_REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 min

// Fetch a fresh Supabase Realtime JWT from the backend and arm the
// realtime client. Graceful no-op when the backend isn't configured
// with `SUPABASE_JWT_SECRET` (returns `{token: null}`).
const armRealtime = async () => {
  try {
    const { data } = await api.get("/api/auth/realtime-token");
    setSupabaseRealtimeAuth(data?.token || null);
    return !!data?.token;
  } catch { /* anonymous or backend unreachable — leave realtime calm */ }
  return false;
};

export const AuthProvider = ({ children }) => {
  // user states: undefined = checking, null = anonymous, object = signed in
  const [user, setUser] = useState(undefined);
  // Holds the realtime-refresh interval id so we can stop it on logout.
  const refreshTimerRef = useRef(null);

  const stopRealtimeRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const startRealtimeRefresh = useCallback(() => {
    stopRealtimeRefresh();
    // Interval only — initial arm is done by the caller (login / hydrate)
    // so we don't double-fire the request.
    refreshTimerRef.current = setInterval(armRealtime, REALTIME_REFRESH_INTERVAL_MS);
  }, [stopRealtimeRefresh]);

  const hydrate = useCallback(async () => {
    // Cookie-only auth — we cannot inspect the httpOnly access_token cookie
    // from JS so the only way to know if the user is signed in is to ask
    // the backend. A 200 means we have a valid cookie; anything else means
    // anonymous.
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);
      // Rearm Supabase Realtime so postgres_changes events keep flowing
      // through RLS-protected publications across page refreshes. We
      // fire-and-forget this so the auth gate flips immediately and the
      // protected pages (Director Centre, Deal Room) can start their own
      // operational fetches without waiting on a non-critical token.
      // The refresh interval arms only when the initial mint succeeds
      // so we don't poll a backend that isn't configured for realtime.
      armRealtime().then((armed) => {
        if (armed) startRealtimeRefresh();
      });
    } catch (_e) {
      setUser(null);
      setSupabaseRealtimeAuth(null);
      stopRealtimeRefresh();
    }
  }, [startRealtimeRefresh, stopRealtimeRefresh]);

  useEffect(() => { hydrate(); }, [hydrate]);
  useEffect(() => () => stopRealtimeRefresh(), [stopRealtimeRefresh]);

  // ─── Split-brain rescue ────────────────────────────────────────────────
  // The api wrapper emits `auth:session-expired` whenever a protected
  // request (anything other than the auth surfaces themselves) returns
  // 401. That means the access_token cookie is invalid even though our
  // local React state still says we're signed in — classic split-brain.
  // We respond by clearing the user, showing a single calm toast, and
  // navigating to /login with a `?reason=expired` hint. The
  // `RequireAuth` guard already redirects anonymous users, so this is
  // belt-and-braces: it makes sure the UI never lies.
  useEffect(() => {
    const onExpired = () => {
      // Avoid noisy double-firing when already anonymous.
      if (user === null) return;
      // Public token-scoped surfaces are intentionally anonymous —
      // exit silently. Don't logout, don't redirect, don't toast.
      const earlyPath = window.location.pathname + window.location.search;
      if (
        earlyPath.startsWith("/client-view/by-token/") ||
        earlyPath.startsWith("/solicitor-portal/")
      ) {
        return;
      }
      // Best-effort server-side cookie purge so the stale
      // access_token / csrf_token are unset on the response. This
      // matters for the rare race where a background request
      // re-fires before our hard redirect lands — the next request
      // sees no cookie at all and 401s cleanly instead of looking
      // half-authenticated. We do NOT await it: the redirect must
      // win.
      try { api.post("/api/auth/logout").catch(() => {}); } catch (_e) { /* noop */ }
      // Drop any stored Bearer token so the next request goes clean.
      clearBearerToken();
      // Trigger the location navigation FIRST so it wins over the
      // <RequireAuth> Navigate(to="/login") that fires immediately
      // after we clear the user state below. Otherwise React Router
      // routes to plain /login without our `reason=expired&next=…`
      // query string.
      try {
        const path = window.location.pathname + window.location.search;
        const isAuthRoute = /^\/(login|signup|forgot|reset|verify|magic|accept-invite|join-deal)/.test(path);
        // Public, token-scoped surfaces (client magic-link · solicitor portal)
        // are intentionally anonymous. A stray 401 from a global widget on
        // these pages must never bounce the recipient to /login — they
        // weren't signed in to begin with.
        const isPublicTokenRoute =
          path.startsWith("/client-view/by-token/") ||
          path.startsWith("/solicitor-portal/");
        if (!isAuthRoute && !isPublicTokenRoute) {
          const next = encodeURIComponent(path);
          window.location.assign(`/login?reason=expired&next=${next}`);
        }
      } catch (_e) { /* noop */ }
      // Clear the in-memory session so any code path that still
      // reads `user` between now and the navigation sees anonymous.
      setUser(null);
      setSupabaseRealtimeAuth(null);
      stopRealtimeRefresh();
      try {
        toast.message("Session expired", {
          description: "Please sign in again.",
          duration: 4500,
        });
      } catch (_e) { /* noop */ }
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
  }, [user, stopRealtimeRefresh]);

  // Silent push resubscribe when an authenticated session is detected
  // and the user has previously granted browser permission. Keeps the
  // server-side subscription record fresh across site-data clears or
  // device migrations.
  useEffect(() => {
    if (!user || !user.id) return;
    let alive = true;
    (async () => {
      try {
        const { refreshSubscriptionSilently } = await import("../lib/pushSubscription");
        if (alive) await refreshSubscriptionSilently();
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    // Capture the Bearer token so requests survive environments where the
    // httpOnly cookie is dropped by ingress (e.g. Cloudflare wildcard CORS).
    if (data?.access_token) setBearerToken(data.access_token);
    setUser(data.user);
    // Login response carries the realtime token directly when the
    // backend is configured; otherwise the hydrate path's
    // `armRealtime()` will pick it up.
    setSupabaseRealtimeAuth(data?.supabase_token || null);
    if (data?.supabase_token) startRealtimeRefresh();
    return data.user;
  }, [startRealtimeRefresh]);

  const register = useCallback(async ({ email, password, full_name, org_slug }) => {
    const { data } = await api.post("/api/auth/register", { email, password, full_name, org_slug });
    if (data?.access_token) setBearerToken(data.access_token);
    setUser(data.user);
    setSupabaseRealtimeAuth(data?.supabase_token || null);
    if (data?.supabase_token) startRealtimeRefresh();
    return data.user;
  }, [startRealtimeRefresh]);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await api.post("/api/auth/forgot-password", { email });
    return data;
  }, []);

  const resetPassword = useCallback(async ({ token, new_password }) => {
    const { data } = await api.post("/api/auth/reset-password", { token, new_password });
    if (data?.access_token) setBearerToken(data.access_token);
    setUser(data.user);
    setSupabaseRealtimeAuth(data?.supabase_token || null);
    if (data?.supabase_token) startRealtimeRefresh();
    return data.user;
  }, [startRealtimeRefresh]);

  const logout = useCallback(async () => {
    // Best-effort: remove the server-side push subscription so this device
    // stops receiving operational pushes after sign-out.
    try {
      const { unsubscribeFromPush } = await import("../lib/pushSubscription");
      await unsubscribeFromPush();
    } catch { /* noop */ }
    try { await api.post("/api/auth/logout"); } catch (_e) { /* noop */ }
    clearBearerToken();
    setUser(null);
    setSupabaseRealtimeAuth(null);
    stopRealtimeRefresh();
  }, [stopRealtimeRefresh]);

  const value = useMemo(
    () => ({ user, login, register, logout, forgotPassword, resetPassword, refresh: hydrate, apiError }),
    [user, login, register, logout, forgotPassword, resetPassword, hydrate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Route guard ────────────────────────────────────────────────────────────
export const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--cf-app-bg)" }} data-testid="auth-loading">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 mx-auto flex items-center justify-center cf-shadow-md">
            <Loader2 size={20} className="text-white animate-spin" />
          </div>
          <p className="mt-4 text-[12.5px] text-slate-500 font-semibold tracking-wide">Verifying your session…</p>
        </div>
      </div>
    );
  }

  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Soft-block: enforce email verification gate everywhere except the verify
  // page itself. The /api/* interceptor catches server-side enforcement; this
  // catches navigation / direct deep-links so users see the gate before they
  // hit a 403.
  if (
    user.email_verification_required &&
    user.email_verified === false &&
    !location.pathname.startsWith("/verify-email")
  ) {
    return <Navigate to="/verify-email" replace state={{ from: location.pathname }} />;
  }

  return children;
};
